import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://culxgyrufratlvhnyhck.supabase.co';
const supabaseAnonKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1bHhneXJ1ZnJhdGx2aG55aGNrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM0MjY5MTEsImV4cCI6MjA4OTAwMjkxMX0.2hbZffPKfQHlDHSVXWKp9jgZV0LnhNrEN2G8hcC-1Go';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── User Profile ─────────────────────────────────────────────────────────────

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

// Alias for backward compatibility - AuthContext uses this
export async function getCurrentUser() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) return { user: null, profile: null };

  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*, organizations(name)')
    .eq('id', user.id)
    .single();

  return { user, profile, error: profileError };
}

// Build a normalized currentUser object from Supabase auth user + public.users row
// Uses backend API to bypass RLS issues
export async function buildCurrentUser(authUser) {
  try {
    // Get the current session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No session token available');
    }
    
    // Call backend API which uses service role (bypasses RLS)
    const API_URL = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const { user } = await response.json();
    console.log('[buildCurrentUser] Got user from API:', user);
    
    return {
      id: user.id,
      email: user.email,
      name: user.name || '',
      orgId: user.orgId || null,
      orgName: user.orgName || null,
      role: user.role || 'member',
      avatarUrl: null,
    };
  } catch (err) {
    console.error('[buildCurrentUser] Error:', err);
    // Fallback to basic info from auth user
    return {
      id: authUser.id,
      email: authUser.email,
      name: authUser.user_metadata?.full_name || '',
      orgId: null,
      orgName: null,
      role: 'member',
      avatarUrl: null,
    };
  }
}

// ─── Organization ─────────────────────────────────────────────────────────────

export const ORG_TYPES = [
  { value: 'business_broker', label: 'Business Broker', description: 'Facilitating the buying and selling of businesses' },
  { value: 'pe', label: 'Private Equity', description: 'Investing in and acquiring private companies' },
  { value: 'vc', label: 'Venture Capital', description: 'Funding early-stage and growth companies' },
  { value: 'corp_dev', label: 'Corporate Development', description: 'M&A and strategic investments for a corporation' },
  { value: 'family_office', label: 'Family Office', description: 'Managing investments for a high-net-worth family' },
  { value: 'real_estate', label: 'Real Estate', description: 'Real estate acquisitions and investments' },
  { value: 'platform', label: 'Platform Company', description: 'Technology platform or software business' },
];

export async function getOrgById(orgId) {
  if (!orgId) return null;
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', orgId)
    .single();
  if (error) return null;
  return data;
}

export async function getOrgMembers(orgId) {
  const { data, error } = await supabase
    .from('users')
    .select('id, email, full_name, role, created_at, avatar_url')
    .eq('org_id', orgId);
  if (error) throw error;
  return data || [];
}

// ─── Invites ──────────────────────────────────────────────────────────────────

export async function getInviteByToken(token) {
  const { data, error } = await supabase
    .from('org_invites')
    .select('*, organizations(id, name, type)')
    .eq('token', token)
    .eq('status', 'pending')
    .single();
  if (error) throw error;
  return data;
}

// ─── Permissions ──────────────────────────────────────────────────────────────

export const RESOURCES = ['pipeline', 'contacts', 'documents', 'deals', 'settings', 'summaries', 'library', 'chats'];
export const ACTIONS = ['view', 'edit', 'delete', 'admin'];

export const DEFAULT_PERMISSIONS = {
  admin: { view: true, edit: true, delete: true, admin: true },
  member: { view: true, edit: true, delete: false, admin: false },
  viewer: { view: true, edit: false, delete: false, admin: false },
};

export async function getUserPermissions(userId) {
  const { data, error } = await supabase
    .from('user_permissions')
    .select('*')
    .eq('user_id', userId);
  if (error) return [];
  return data || [];
}
